import { PxtraApiClientLegacy } from '@pxtra-client-lib-legacy';
import {
  BenefitCalculatorRequest,
  BenefitCalculatorResponse,
  CompanyBenefitSettings,
  CompanyBenefitStats,
  CustomBenefitStatusEnum,
  IconEnum
} from '@pxtra-client-lib-legacy/gen';
import { makeAutoObservable, runInAction, when } from 'mobx';
import AuthenticationStore from './authenticationStore';
import CompanyStore from './companyStore';
import { GridRowModel } from '@mui/x-data-grid-pro';

export type CustomBenefit = {
  id: string;
  name: string;
  status: CustomBenefitStatusEnum;
  icon: IconEnum;
};

export type CompanyBenefitKeys = Exclude<keyof CompanyBenefitSettings, 'custom'>;

export const allBenefits: CompanyBenefitKeys[] = [
  'betreuungskosten',
  'erholungsbeihilfe',
  'essenszuschuss',
  'fahrtkosten',
  'internetpauschale',
  'öpnv_tickets',
  'telefonkosten',
  'sachbezug',
  'mpp',
  'bav',
  'bkv',
  'jobbike'
];

class BenefitsStore {
  private apiClient: PxtraApiClientLegacy;

  private _stats?: CompanyBenefitStats;
  private _calculation?: BenefitCalculatorResponse;
  private _currentCustomBenefit?: CustomBenefit;

  constructor(
    apiClient: PxtraApiClientLegacy,
    private companyStore: CompanyStore,
    private authenticationStore: AuthenticationStore
  ) {
    this.apiClient = apiClient;
    makeAutoObservable(this);
    when(
      () => !this.authenticationStore.isLoggedIn,
      () => {
        this._clear();
      }
    );
  }

  private _clear() {
    this._stats = undefined;
    this._calculation = undefined;
    this._currentCustomBenefit = undefined;
  }

  public get stats() {
    return this._stats;
  }

  public get calculation() {
    return this._calculation;
  }

  public get currentCustomBenefit() {
    return this._currentCustomBenefit;
  }

  public async fetchBenefitsStats() {
    if (!this.companyStore.company) throw Error('No company selected');
    const res = await this.apiClient.benefits.benefitsStatsRetrieve({
      company: this.companyStore.company.id
    });
    runInAction(() => (this._stats = res));
  }

  public async calculateBenefit(request: BenefitCalculatorRequest) {
    return this.apiClient.benefits
      .benefitsCalculatorCreate({ requestBody: request })
      .then((result) => runInAction(() => (this._calculation = result)))
      .catch((e) => {
        throw e;
      });
  }

  public async benefitInUse(benefit: keyof CompanyBenefitStats) {
    if (!this._stats) await this.fetchBenefitsStats();
    if (this._stats) {
      if (benefit === 'employee_count') return;
      return !!this._stats?.[benefit].active_count;
    }
  }

  public async updateCurrent(customBenefit: CustomBenefit) {
    runInAction(
      () =>
        (this._currentCustomBenefit = {
          id: customBenefit.id,
          name: customBenefit.name,
          status: customBenefit.status,
          icon: customBenefit.icon
        })
    );
  }

  public async delete(customBenefit: GridRowModel) {
    if (!this.companyStore.company) throw Error('No company selected');
    await this.apiClient.companies.companiesBenefitSettingsCustomDestroy({
      id: this.companyStore.company.id,
      benefitId: customBenefit.id
    });
    runInAction(() => (this._currentCustomBenefit = undefined));
    await this.companyStore.fetchCustomBenefitSettings();
  }

  public async discardDraft(customBenefit: GridRowModel) {
    await this.apiClient.benefits.benefitsCustomEmployeeAssignmentsDeleteCreate({
      benefitId: customBenefit.id,
      requestBody: {
        assignment_ids: [],
        all: true
      }
    });

    await this.companyStore.fetchCustomBenefitSettings();
  }
}

export default BenefitsStore;
